import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  TextField,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useHasPermission } from '../../hooks/usePermission';
import {
  fetchBalance,
  addManualCredits,
  subtractManualCredits,
  fetchTransactionHistory
} from '../../redux/slices/creditSlice';
import userService from '../../services/userService';
import companyService from '../../services/companyService';
import ForbiddenPage from '../ForbiddenPage';
import alertify from 'alertifyjs';

/**
 * CreditManagePage - Admin manual credit management page
 * Phase 7: Credit System
 * 
 * Features:
 * - User/Company selection (Autocomplete)
 * - Current balance display
 * - Add credits form
 * - Subtract credits form
 * - Transaction history (manual operations only)
 * 
 * Permission: credits.manage
 * ⚠️ CRITICAL: Manual operations are independent of payment gateway status
 */
const CreditManagePage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const hasPermission = useHasPermission('credits.manage');

  const { balance, transactions, loading } = useSelector(state => state.credits);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [addFormData, setAddFormData] = useState({ amount: '', description: '' });
  const [subtractFormData, setSubtractFormData] = useState({ amount: '', description: '' });
  const [addFormErrors, setAddFormErrors] = useState({});
  const [subtractFormErrors, setSubtractFormErrors] = useState({});

  // Load users and companies
  useEffect(() => {
    if (hasPermission) {
      setUsersLoading(true);
      // Sadece bireysel kullanıcıları getir (companyId null olanlar)
      // Şirket kullanıcıları zaten şirket filtresi ile yönetiliyor
      userService.getUsers({ individualOnly: true, limit: 1000 })
        .then(result => {
          setUsers(result.users || []);
        })
        .catch(err => {
          console.error('Error loading users:', err);
          alertify.error('Kullanıcılar yüklenemedi');
        })
        .finally(() => setUsersLoading(false));

      setCompaniesLoading(true);
      companyService.getCompanies()
        .then(result => {
          setCompanies(Array.isArray(result) ? result : (result.companies || []));
        })
        .catch(err => {
          console.error('Error loading companies:', err);
          alertify.error('Şirketler yüklenemedi');
        })
        .finally(() => setCompaniesLoading(false));
    }
  }, [hasPermission]);

  // Load balance and transactions when user/company is selected
  useEffect(() => {
    if (hasPermission && (selectedUser || selectedCompany)) {
      const userId = selectedUser?._id || null;
      const companyId = selectedCompany?._id || null;
      
      dispatch(fetchBalance({ userId, companyId }));
      dispatch(fetchTransactionHistory({
        userId,
        companyId,
        type: undefined,
        startDate: undefined,
        endDate: undefined
      }, { page: 1, limit: 50 }));
    }
  }, [dispatch, hasPermission, selectedUser, selectedCompany]);

  const handleUserChange = (event, value) => {
    setSelectedUser(value);
    setSelectedCompany(null); // Clear company when user is selected
    setAddFormData({ amount: '', description: '' });
    setSubtractFormData({ amount: '', description: '' });
    setAddFormErrors({});
    setSubtractFormErrors({});
  };

  const handleCompanyChange = (event, value) => {
    setSelectedCompany(value);
    setSelectedUser(null); // Clear user when company is selected
    setAddFormData({ amount: '', description: '' });
    setSubtractFormData({ amount: '', description: '' });
    setAddFormErrors({});
    setSubtractFormErrors({});
  };

  const validateAddForm = () => {
    const errors = {};
    if (!addFormData.amount || parseFloat(addFormData.amount) <= 0) {
      errors.amount = 'Miktar 0\'dan büyük olmalıdır';
    }
    if (!addFormData.description || addFormData.description.trim() === '') {
      errors.description = 'Açıklama zorunludur';
    }
    setAddFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSubtractForm = () => {
    const errors = {};
    if (!subtractFormData.amount || parseFloat(subtractFormData.amount) <= 0) {
      errors.amount = 'Miktar 0\'dan büyük olmalıdır';
    }
    if (selectedUser && balance < parseFloat(subtractFormData.amount)) {
      errors.amount = 'Yetersiz bakiye';
    }
    if (selectedCompany && balance < parseFloat(subtractFormData.amount)) {
      errors.amount = 'Yetersiz bakiye';
    }
    if (!subtractFormData.description || subtractFormData.description.trim() === '') {
      errors.description = 'Açıklama zorunludur';
    }
    setSubtractFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = async () => {
    if (!selectedUser && !selectedCompany) {
      alertify.error('Lütfen bir kullanıcı veya şirket seçin');
      return;
    }

    if (!validateAddForm()) {
      return;
    }

    try {
      await dispatch(addManualCredits({
        userId: selectedUser?._id || null,
        companyId: selectedCompany?._id || null,
        amount: parseFloat(addFormData.amount),
        description: addFormData.description.trim()
      })).unwrap();

      alertify.success('Kredi başarıyla yüklendi');
      setAddFormData({ amount: '', description: '' });
      setAddFormErrors({});
      
      // Refresh balance and transactions
      const userId = selectedUser?._id || null;
      const companyId = selectedCompany?._id || null;
      dispatch(fetchBalance({ userId, companyId }));
      dispatch(fetchTransactionHistory({
        userId,
        companyId,
        type: undefined,
        startDate: undefined,
        endDate: undefined
      }, { page: 1, limit: 50 }));
    } catch (error) {
      alertify.error(error.message || 'Kredi yükleme başarısız');
    }
  };

  const handleSubtract = async () => {
    if (!selectedUser && !selectedCompany) {
      alertify.error('Lütfen bir kullanıcı veya şirket seçin');
      return;
    }

    if (!validateSubtractForm()) {
      return;
    }

    try {
      await dispatch(subtractManualCredits({
        userId: selectedUser?._id || null,
        companyId: selectedCompany?._id || null,
        amount: parseFloat(subtractFormData.amount),
        description: subtractFormData.description.trim()
      })).unwrap();

      alertify.success('Kredi başarıyla çıkarıldı');
      setSubtractFormData({ amount: '', description: '' });
      setSubtractFormErrors({});
      
      // Refresh balance and transactions
      const userId = selectedUser?._id || null;
      const companyId = selectedCompany?._id || null;
      dispatch(fetchBalance({ userId, companyId }));
      dispatch(fetchTransactionHistory({
        userId,
        companyId,
        type: undefined,
        startDate: undefined,
        endDate: undefined
      }, { page: 1, limit: 50 }));
    } catch (error) {
      alertify.error(error.message || 'Kredi çıkarma başarısız');
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      purchase: 'Satın Alma',
      usage: 'Kullanım',
      manual_add: 'Manuel Yükleme',
      manual_subtract: 'Manuel Çıkarma',
      refund: 'İade'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      purchase: 'success',
      usage: 'error',
      manual_add: 'info',
      manual_subtract: 'warning',
      refund: 'default'
    };
    return colors[type] || 'default';
  };

  // Show all transaction types (not just manual operations)
  // Admin can see all transactions for selected user/company
  const allTransactions = transactions;

  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.goBack()}
          >
            Geri Dön
          </Button>
          <AccountBalanceIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Manuel Kredi Yönetimi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kullanıcı veya şirket bakiyelerini manuel olarak yönetin
            </Typography>
          </Box>
        </Box>

        {/* User/Company Selection */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Kullanıcı veya Şirket Seçin
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={users}
                loading={usersLoading}
                getOptionLabel={(option) => 
                  `${option.firstName || ''} ${option.lastName || ''} (${option.email || ''})`.trim()
                }
                value={selectedUser}
                onChange={handleUserChange}
                disabled={!!selectedCompany}
                renderInput={(params) => {
                  const { disabled: _, ...textFieldParams } = params;
                  return (
                    <TextField
                      {...textFieldParams}
                      label="Kullanıcı"
                      placeholder="Kullanıcı ara..."
                    />
                  );
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={companies}
                loading={companiesLoading}
                getOptionLabel={(option) => option.companyName || option.name || ''}
                value={selectedCompany}
                onChange={handleCompanyChange}
                disabled={!!selectedUser}
                renderInput={(params) => {
                  const { disabled: _, ...textFieldParams } = params;
                  return (
                    <TextField
                      {...textFieldParams}
                      label="Şirket"
                      placeholder="Şirket ara..."
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Current Balance */}
        {(selectedUser || selectedCompany) && (
          <>
            <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent>
                <Typography variant="h4" align="center">
                  Mevcut Bakiye: {balance} Kredi
                </Typography>
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  {selectedUser 
                    ? `${selectedUser.firstName} ${selectedUser.lastName || ''}`.trim()
                    : selectedCompany?.companyName || selectedCompany?.name}
                </Typography>
              </CardContent>
            </Card>

            {/* Add Credits Form */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                <AddIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Kredi Yükle
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Miktar"
                    type="number"
                    value={addFormData.amount}
                    onChange={(e) => {
                      setAddFormData({ ...addFormData, amount: e.target.value });
                      if (addFormErrors.amount) {
                        setAddFormErrors({ ...addFormErrors, amount: null });
                      }
                    }}
                    error={!!addFormErrors.amount}
                    helperText={addFormErrors.amount}
                    inputProps={{ min: 0, step: '0.01' }}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Açıklama"
                    multiline
                    rows={3}
                    value={addFormData.description}
                    onChange={(e) => {
                      setAddFormData({ ...addFormData, description: e.target.value });
                      if (addFormErrors.description) {
                        setAddFormErrors({ ...addFormErrors, description: null });
                      }
                    }}
                    error={!!addFormErrors.description}
                    helperText={addFormErrors.description || 'Açıklama zorunludur (örn: "Nakit ödeme karşılığı", "Hediye kredi")'}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    disabled={loading}
                  >
                    Kredi Yükle
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Subtract Credits Form */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                <RemoveIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Kredi Çıkar
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" sx={{ mb: 2 }}>
                Yanlış bakiye yükleme durumunda bu formu kullanarak düzeltme yapabilirsiniz.
                Örnek: 100 kredi yüklendi, 50 olmalıydı → 50 kredi çıkarın.
              </Alert>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Miktar"
                    type="number"
                    value={subtractFormData.amount}
                    onChange={(e) => {
                      setSubtractFormData({ ...subtractFormData, amount: e.target.value });
                      if (subtractFormErrors.amount) {
                        setSubtractFormErrors({ ...subtractFormErrors, amount: null });
                      }
                    }}
                    error={!!subtractFormErrors.amount}
                    helperText={subtractFormErrors.amount}
                    inputProps={{ min: 0, step: '0.01' }}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Açıklama"
                    multiline
                    rows={3}
                    value={subtractFormData.description}
                    onChange={(e) => {
                      setSubtractFormData({ ...subtractFormData, description: e.target.value });
                      if (subtractFormErrors.description) {
                        setSubtractFormErrors({ ...subtractFormErrors, description: null });
                      }
                    }}
                    error={!!subtractFormErrors.description}
                    helperText={subtractFormErrors.description || 'Açıklama zorunludur (örn: "Yanlış yükleme düzeltmesi", "İade")'}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<RemoveIcon />}
                    onClick={handleSubtract}
                    disabled={loading}
                  >
                    Kredi Çıkar
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Transaction History */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Geçmiş İşlemler
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {loading && allTransactions.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : allTransactions.length === 0 ? (
                <Alert severity="info">
                  Henüz işlem geçmişi bulunmamaktadır.
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tarih</strong></TableCell>
                        <TableCell><strong>Tip</strong></TableCell>
                        <TableCell><strong>Miktar</strong></TableCell>
                        <TableCell><strong>Bakiye Öncesi</strong></TableCell>
                        <TableCell><strong>Bakiye Sonrası</strong></TableCell>
                        <TableCell><strong>Açıklama</strong></TableCell>
                        <TableCell><strong>Paket</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allTransactions.map((transaction) => (
                        <TableRow key={transaction._id}>
                          <TableCell>
                            {format(new Date(transaction.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getTypeLabel(transaction.type)}
                              color={getTypeColor(transaction.type)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={transaction.type === 'usage' || transaction.type === 'manual_subtract' ? 'error.main' : 'success.main'}
                              fontWeight="bold"
                            >
                              {transaction.type === 'usage' || transaction.type === 'manual_subtract' ? '-' : '+'}
                              {transaction.amount.toFixed(2)} Kredi
                            </Typography>
                          </TableCell>
                          <TableCell>{transaction.balanceBefore.toFixed(2)} Kredi</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {transaction.balanceAfter.toFixed(2)} Kredi
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {transaction.description || transaction.packageId?.name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {transaction.packageId ? (
                              <Typography variant="body2" color="primary">
                                {transaction.packageId.name || '-'}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}

        {/* No Selection Message */}
        {!selectedUser && !selectedCompany && (
          <Alert severity="info">
            Lütfen yukarıdan bir kullanıcı veya şirket seçin
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default CreditManagePage;

